import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import Markdown from 'markdown-to-jsx';
import './chat.css';

// Best-effort conversion of backend message content into readable text.
// This prevents `[object Object]` from appearing when the backend sends structured payloads
// (e.g., Microsoft Graph `body: { contentType, content }` objects).
const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(
      value,
      (_k, v) => (typeof v === 'bigint' ? v.toString() : v),
      2,
    );
  } catch (_err) {
    try {
      return String(value);
    } catch (_err2) {
      return '';
    }
  }
};

const stripHtmlToText = (html) => {
  if (typeof html !== 'string') return '';
  // Browser-only; DOMParser exists in the React app runtime.
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc?.body?.textContent;
    return (text && text.trim()) ? text : html;
  } catch (_err) {
    return html;
  }
};

const graphBodyToText = (body) => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const content = body.content;
  if (typeof content !== 'string') return null;
  const ct = String(body.contentType || '').toLowerCase();
  if (ct === 'html') return stripHtmlToText(content);
  return content;
};

const findFirstGraphBodyContent = (value, maxDepth = 4) => {
  const seen = new Set();

  const walk = (node, depth) => {
    if (!node || typeof node !== 'object') return null;
    if (seen.has(node)) return null;
    seen.add(node);
    if (depth > maxDepth) return null;

    // Direct Graph body shape
    const direct = graphBodyToText(node);
    if (direct) return direct;

    // Common nesting: { body: {...} }
    if (node.body && typeof node.body === 'object') {
      const fromBody = graphBodyToText(node.body);
      if (fromBody) return fromBody;
    }

    // Search children
    const entries = Array.isArray(node) ? node : Object.values(node);
    for (let i = 0; i < entries.length; i += 1) {
      const child = entries[i];
      if (child && typeof child === 'object') {
        const found = walk(child, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  return walk(value, 0);
};

const coerceMessageContentToText = (content) => {
  if (content === null || content === undefined) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);

  // OpenAI-style or other structured arrays of blocks
  if (Array.isArray(content)) {
    const joined = content
      .map((part) => {
        if (typeof part === 'string') return part;
        if (part && typeof part === 'object') {
          if (typeof part.text === 'string') return part.text;
          if (typeof part.content === 'string') return part.content;
        }
        return '';
      })
      .join('')
      .trim();
    return joined || safeJsonStringify(content);
  }

  if (typeof content === 'object') {
    // Common shallow fields
    if (typeof content.text === 'string') {
      const t = content.text;
      if (t.includes('[object Object]')) {
        const nestedBody = findFirstGraphBodyContent(content);
        if (nestedBody) return t.replace('[object Object]', nestedBody);
      }
      return t;
    }
    if (typeof content.content === 'string') {
      const t = content.content;
      if (t.includes('[object Object]')) {
        const nestedBody = findFirstGraphBodyContent(content);
        if (nestedBody) return t.replace('[object Object]', nestedBody);
      }
      return t;
    }
    if (typeof content.message === 'string') {
      const t = content.message;
      if (t.includes('[object Object]')) {
        const nestedBody = findFirstGraphBodyContent(content);
        if (nestedBody) return t.replace('[object Object]', nestedBody);
      }
      return t;
    }

    // Graph body objects or nested Graph body
    const directBody = graphBodyToText(content);
    if (directBody) return directBody;
    const nestedBody = findFirstGraphBodyContent(content);
    if (nestedBody) return nestedBody;

    return safeJsonStringify(content);
  }

  try {
    return String(content);
  } catch (_err) {
    return '';
  }
};

// Deterministic small string hash for stable React keys when message ids are missing.
const hashString = (input) => {
  const s = String(input || '');
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h) ^ s.charCodeAt(i); // djb2 xor
  }
  return (h >>> 0).toString(16);
};

// Robustly extracts the fan-out payload block even when nested code fences appear inside JSON strings.
const extractFanOutPayload = (text) => {
  if (typeof text !== 'string') return null;
  const payloadMarker = 'Payload:';
  const markerIndex = text.indexOf(payloadMarker);
  if (markerIndex === -1) return null;

  const fenceStart = text.indexOf('```json', markerIndex);
  if (fenceStart === -1) return null;

  const firstBrace = text.indexOf('{', fenceStart);
  if (firstBrace === -1) return null;

  let depth = 0;
  let inString = false;
  let jsonEnd = -1;
  let prevChar = '';

  for (let idx = firstBrace; idx < text.length; idx += 1) {
    const ch = text[idx];
    if (inString) {
      if (ch === '"' && prevChar !== '\\') {
        inString = false;
      }
    } else {
      if (ch === '"') {
        inString = true;
      } else if (ch === '{') {
        depth += 1;
      } else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          jsonEnd = idx + 1;
          break;
        }
      }
    }
    prevChar = ch;
  }

  if (jsonEnd === -1) return null;

  const jsonText = text.slice(firstBrace, jsonEnd);

  // Determine trailing content after the closing code fence, if present
  let remainder = text.slice(jsonEnd);
  const fenceCloseIdx = remainder.indexOf('```');
  if (fenceCloseIdx !== -1) {
    remainder = remainder.slice(fenceCloseIdx + 3);
  }

  return {
    summary: text.slice(0, markerIndex).trim(),
    jsonText,
    trailing: remainder.trim(),
  };
};

const escapeUnderscoresOutsideCode = (value) => {
  if (typeof value !== 'string' || value.length === 0) return value;

  let out = '';
  let inFence = false;
  let inInline = false;

  for (let i = 0; i < value.length; i += 1) {
    if (!inInline && value.startsWith('```', i)) {
      inFence = !inFence;
      out += '```';
      i += 2;
      continue;
    }

    const ch = value[i];

    if (!inFence && ch === '`') {
      inInline = !inInline;
      out += ch;
      continue;
    }

    if (!inFence && !inInline && ch === '_') {
      if (i > 0 && value[i - 1] === '\\') {
        out += '_';
      } else {
        out += '\\_';
      }
      continue;
    }

    out += ch;
  }

  return out;
};

const normalizeForMarkdown = (value) => {
  if (typeof value !== 'string') return value;
  const normalized = value.includes('\\n') ? value.replace(/\\n/g, '\n') : value;
  return escapeUnderscoresOutsideCode(normalized);
};

// Only for complete_task Action_Response wrappers: remove the leading header line
// (e.g., "Action Response for 'complete_task':") so the final answer starts cleanly.
// This must ONLY apply at the very beginning of the message.
const stripLeadingCompleteTaskHeaderLine = (text) => {
  if (typeof text !== 'string') return '';
  const headerRe = /^\s*Action[_ ]Response\s+for\s+['"]?complete_task['"]?\s*:\s*(?:\r?\n)?/i;
  if (!headerRe.test(text)) return text;
  return text.replace(headerRe, '');
};

// Best-effort split of a top-level "References:" section for complete_task outputs.
// Safety properties:
// - Only splits outside fenced code blocks.
// - If not found (or empty), returns null and the caller renders verbatim.
// - Does not alter content; it only partitions it.
const splitTopLevelReferencesSection = (text) => {
  if (typeof text !== 'string') return null;
  const lines = text.split(/\r?\n/);
  let inFence = false;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (!inFence && /^\s*References\s*:\s*$/i.test(line)) {
      const main = lines.slice(0, i).join('\n').trimEnd();
      const references = lines.slice(i).join('\n').trim();
      if (!references) return null;
      return { main, references };
    }
  }
  return null;
};

const parseActionPayload = (raw) => {
  if (raw === null || raw === undefined) return null;

  const buildResult = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const thoughtValue = payload.thought;
    const actionName = payload.name;
    const parameters = payload.parameters;
    if (thoughtValue === undefined && actionName === undefined) return null;

    let normalizedParameters;
    if (
      typeof parameters === 'string' ||
      typeof parameters === 'number' ||
      typeof parameters === 'boolean' ||
      parameters === null
    ) {
      normalizedParameters = parameters;
    } else if (Array.isArray(parameters) || typeof parameters === 'object') {
      normalizedParameters = parameters;
    } else if (parameters !== undefined) {
      normalizedParameters = String(parameters);
    } else {
      normalizedParameters = undefined;
    }

    return {
      thought: typeof thoughtValue === 'string' ? thoughtValue : thoughtValue !== undefined && thoughtValue !== null ? String(thoughtValue) : '',
      action: {
        name: typeof actionName === 'string' ? actionName : actionName !== undefined && actionName !== null ? String(actionName) : '',
        parameters: normalizedParameters,
      },
    };
  };

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return buildResult(raw);
  }

  if (typeof raw !== 'string') return null;

  let stripped = raw.trim();
  if (!stripped) return null;

  if (stripped.startsWith('```')) {
    const firstNewline = stripped.indexOf('\n');
    if (firstNewline !== -1) {
      stripped = stripped.slice(firstNewline + 1);
    } else {
      stripped = stripped.slice(3);
    }
    if (stripped.endsWith('```')) {
      stripped = stripped.slice(0, -3);
    }
    stripped = stripped.trim();
  }

  try {
    const parsed = JSON.parse(stripped);
    return buildResult(parsed);
  } catch (_err) {
    return null;
  }
};

const formatParamScalar = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return String(value);
  }
};

const stripCodeFences = (text) => {
  if (typeof text !== 'string') return '';
  let s = text.trim();
  if (!s.startsWith('```')) return s;
  // Remove leading fence line (``` or ```lang)
  const firstNewline = s.indexOf('\n');
  if (firstNewline !== -1) {
    s = s.slice(firstNewline + 1);
  } else {
    s = s.slice(3);
  }
  // Remove trailing fence
  if (s.endsWith('```')) s = s.slice(0, -3);
  return s.trim();
};

const tryParseLooseJson = (raw) => {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;

  // First try strict JSON
  try {
    return JSON.parse(s);
  } catch (_err) {
    // ignore
  }

  // If this is a larger string that *contains* a JSON-like payload, try extracting the first
  // balanced {...} or [...] region and parsing that.
  const extractBalancedRegion = () => {
    const startObj = s.indexOf('{');
    const startArr = s.indexOf('[');
    const start = (startObj === -1) ? startArr : (startArr === -1 ? startObj : Math.min(startObj, startArr));
    if (start === -1) return null;

    const open = s[start];
    const close = open === '{' ? '}' : ']';
    let depth = 0;
    let inString = false;
    let prev = '';
    for (let i = start; i < s.length; i += 1) {
      const ch = s[i];
      if (inString) {
        if (ch === '"' && prev !== '\\') inString = false;
      } else {
        if (ch === '"') inString = true;
        else if (ch === open) depth += 1;
        else if (ch === close) {
          depth -= 1;
          if (depth === 0) return s.slice(start, i + 1);
        }
      }
      prev = ch;
    }
    return null;
  };

  const region = extractBalancedRegion();
  if (region) {
    try {
      return JSON.parse(region);
    } catch (_err3) {
      // fall through to python-ish normalization
    }
  }

  // Heuristic for python-ish repr (single quotes, None/True/False)
  // Only attempt when it looks like an object/array.
  const candidate = (s.startsWith('{') || s.startsWith('[')) ? s : (region || '');
  if (!candidate) return null;

  // Robust fallback: parse a limited subset of Python literals (dict/list/strings/bools/None/numbers).
  // This handles outputs like: {'status': 'success', 'output': '{"ok": true, ...}', ...}
  // without trying to naively replace quotes (which breaks embedded JSON strings).
  const parsePythonLiteral = (text) => {
    if (typeof text !== 'string') return null;
    let i = 0;
    const len = text.length;

    const isWs = (ch) => ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r';
    const skipWs = () => { while (i < len && isWs(text[i])) i += 1; };
    const peek = () => text[i];
    const consume = (ch) => {
      if (text[i] !== ch) return false;
      i += 1;
      return true;
    };

    const parseString = () => {
      const quote = peek();
      if (quote !== '"' && quote !== "'") return null;
      i += 1;
      let out = '';
      while (i < len) {
        const ch = text[i];
        if (ch === quote) { i += 1; return out; }
        if (ch === '\\') {
          const next = text[i + 1];
          if (next === undefined) { i += 1; break; }
          // Keep basic escapes; unknown escapes just take the raw char.
          if (next === 'n') out += '\n';
          else if (next === 'r') out += '\r';
          else if (next === 't') out += '\t';
          else out += next;
          i += 2;
          continue;
        }
        out += ch;
        i += 1;
      }
      return null;
    };

    const parseNumber = () => {
      const start = i;
      if (peek() === '-') i += 1;
      let sawDigit = false;
      while (i < len && /[0-9]/.test(text[i])) { i += 1; sawDigit = true; }
      if (i < len && text[i] === '.') {
        i += 1;
        while (i < len && /[0-9]/.test(text[i])) { i += 1; sawDigit = true; }
      }
      if (!sawDigit) { i = start; return null; }
      const rawNum = text.slice(start, i);
      const n = Number(rawNum);
      return Number.isFinite(n) ? n : null;
    };

    const parseIdentifier = () => {
      const start = i;
      while (i < len && /[A-Za-z_]/.test(text[i])) i += 1;
      if (i === start) return null;
      return text.slice(start, i);
    };

    const parseValue = () => {
      skipWs();
      const ch = peek();

      if (ch === '{') return parseDict();
      if (ch === '[') return parseList();
      if (ch === '"' || ch === "'") return parseString();
      if (ch === '-' || (ch >= '0' && ch <= '9')) return parseNumber();

      // Handle python Ellipsis literal
      if (text.slice(i, i + 3) === '...') { i += 3; return '...'; }

      const ident = parseIdentifier();
      if (ident === null) return null;
      if (ident === 'None') return null;
      if (ident === 'True') return true;
      if (ident === 'False') return false;
      // Unknown identifier: treat as string token
      return ident;
    };

    const parseList = () => {
      if (!consume('[')) return null;
      const arr = [];
      skipWs();
      if (consume(']')) return arr;
      while (i < len) {
        const v = parseValue();
        if (v === null && text.slice(i - 4, i) !== 'None') return null;
        arr.push(v);
        skipWs();
        if (consume(']')) return arr;
        if (!consume(',')) return null;
        skipWs();
        // Allow trailing comma
        if (consume(']')) return arr;
      }
      return null;
    };

    const parseDict = () => {
      if (!consume('{')) return null;
      const obj = {};
      skipWs();
      if (consume('}')) return obj;
      while (i < len) {
        skipWs();
        // Keys are usually quoted; allow identifiers too.
        let key;
        if (peek() === '"' || peek() === "'") {
          key = parseString();
        } else {
          key = parseIdentifier();
        }
        if (key === null) return null;
        skipWs();
        if (!consume(':')) return null;
        const value = parseValue();
        if (value === null && text.slice(i - 4, i) !== 'None') return null;
        obj[String(key)] = value;
        skipWs();
        if (consume('}')) return obj;
        if (!consume(',')) return null;
        skipWs();
        // Allow trailing comma
        if (consume('}')) return obj;
      }
      return null;
    };

    skipWs();
    const result = parseValue();
    skipWs();
    if (result === null) return null;
    // Ensure we consumed the full string
    if (i < len) return null;
    return result;
  };

  // Try parsing as a Python literal first.
  const pyParsed = parsePythonLiteral(candidate);
  if (pyParsed !== null) return pyParsed;

  // Last-chance heuristic: attempt simple python-ish normalization for very plain dicts.
  const hasSingleQuotes = candidate.includes("'");
  if (!hasSingleQuotes) return null;
  const hasDoubleQuotes = candidate.includes('"');
  const normalized = (
    (hasDoubleQuotes ? candidate : candidate.replace(/'/g, '"'))
      .replace(/\bNone\b/g, 'null')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
  );
  try {
    return JSON.parse(normalized);
  } catch (_err2) {
    return null;
  }
};

const parseActionResponseMessage = (contentStr) => {
  if (typeof contentStr !== 'string') return null;
  // Only treat as an Action_Response wrapper when it appears at the start of the message.
  // Otherwise we can accidentally hijack normal assistant answers that include references like:
  // "[1] Action Response for 'googleSearch': ..."
  const markerRe = /^\s*Action[_ ]Response\s+for/i;
  const idx = contentStr.search(markerRe);
  if (idx === -1) return null;

  // Expected format from backend:
  // Action_Response for <tool>:
  // ```
  // <result>
  // ```
  const headerLine = contentStr.slice(idx).split('\n')[0] || '';
  const toolMatch = headerLine.match(/Action[_ ]Response\s+for\s+['"]?([^'":\n]+)['"]?\s*:/i);
  const toolName = toolMatch ? toolMatch[1].trim() : '';

  let remainder = contentStr.slice(idx + headerLine.length).trim();
  // Prefer extracting the first fenced block after the header.
  const fenceStart = remainder.indexOf('```');
  if (fenceStart !== -1) {
    remainder = remainder.slice(fenceStart);
  }
  remainder = stripCodeFences(remainder);

  // Many tool results start with "Observation:"; keep it, but present as plain text.
  const payloadText = remainder;
  const parsed = tryParseLooseJson(payloadText);

  return {
    toolName,
    payloadText,
    parsed,
  };
};

function PrettyValue({ value, depth = 0, maxDepth = 3, openAll = false }) {
  if (value === null) return <span className="agent-param-null">null</span>;
  if (value === undefined) return <span className="agent-param-null">—</span>;

  if (typeof value === 'string') {
    const multiline = value.includes('\n') || value.length > 140;
    return multiline
      ? <pre className="agent-param-pre">{value}</pre>
      : <span className="agent-param-text">{value}</span>;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="agent-param-text">{String(value)}</span>;
  }

  // Arrays
  if (Array.isArray(value)) {
    if (depth >= maxDepth) {
      return <pre className="agent-param-pre">{formatParamScalar(value)}</pre>;
    }

    const label = `List (${value.length})`;
    const shouldCollapse = value.length > 8 || depth >= 1;
    return (
      <details className="agent-expand" open={openAll || !shouldCollapse}>
        <summary>{label}</summary>
        <ul className="agent-nested-list">
          {value.map((item, idx) => (
            <li key={`arr-${depth}-${idx}`} className="agent-nested-list-item">
              <PrettyValue value={item} depth={depth + 1} maxDepth={maxDepth} openAll={openAll} />
            </li>
          ))}
        </ul>
      </details>
    );
  }

  // Objects
  if (typeof value === 'object') {
    if (depth >= maxDepth) {
      return <pre className="agent-param-pre">{formatParamScalar(value)}</pre>;
    }

    const entries = Object.entries(value);
    const label = `Details (${entries.length})`;
    const shouldCollapse = entries.length > 10 || depth >= 1;

    return (
      <details className="agent-expand" open={openAll || !shouldCollapse}>
        <summary>{label}</summary>
        <div className="agent-nested-object">
          <table className="agent-params-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([k, v]) => (
                <tr key={`obj-${depth}-${k}`}>
                  <td className="agent-param-key">{k}</td>
                  <td className="agent-param-val">
                    <PrettyValue value={v} depth={depth + 1} maxDepth={maxDepth} openAll={openAll} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    );
  }

  return <span className="agent-param-text">{String(value)}</span>;
}

const looksLikeCode = (text) => {
  if (typeof text !== 'string') return false;
  const s = text.trim();
  if (!s) return false;
  // If it already includes a code fence, treat it as code.
  if (s.includes('```')) return true;

  const lines = s.split('\n');
  if (lines.length < 2) return false;

  // Simple heuristics: common code tokens
  const codeTokens = [
    'def ', 'class ', 'import ', 'from ', 'return ',
    'function ', 'const ', 'let ', 'var ',
    '=>', ';', '{', '}', '</', '/>',
  ];
  const tokenHits = codeTokens.reduce((acc, t) => acc + (s.includes(t) ? 1 : 0), 0);
  // Consider it code-ish if multiple tokens match or braces are frequent.
  const braceCount = (s.match(/[{}]/g) || []).length;
  return tokenHits >= 2 || braceCount >= 6;
};

function ActionResponseCard({ toolName, parsed, payloadText, markdownOptions }) {
  const plain = normalizeForMarkdown(payloadText || '');
  const codeLike = looksLikeCode(String(payloadText || ''));
  const [detailsOpen, setDetailsOpen] = useState(false);

  const summaryText = useMemo(() => {
    if (Array.isArray(parsed)) return `Returned ${parsed.length} item(s).`;
    if (parsed && typeof parsed === 'object') return `Returned ${Object.keys(parsed).length} field(s).`;
    const firstLine = String(plain || '').split('\n').find(Boolean) || '';
    if (firstLine) return firstLine.length > 140 ? `${firstLine.slice(0, 140)}…` : firstLine;
    return 'Response received.';
  }, [parsed, plain]);

  return (
    <div className="agent-response-card">
      <div className="agent-response-header">
        <div className="agent-response-label">Action response</div>
        {toolName ? <span className="agent-action-name">{toolName}</span> : null}
        <button
          type="button"
          className="agent-view-details-btn"
          onClick={() => setDetailsOpen(true)}
          title="View full details"
        >
          View details
        </button>
      </div>

      <div className="agent-response-body">
        <div className="agent-response-summary">{summaryText}</div>
      </div>

      <Modal
        isOpen={detailsOpen}
        onRequestClose={() => setDetailsOpen(false)}
        contentLabel="Action response details"
        className="agent-response-modal"
        overlayClassName="agent-response-overlay"
      >
        <div className="agent-response-modal-header">
          <div className="agent-response-modal-title">
            Action response{toolName ? ` — ${toolName}` : ''}
          </div>
          <button
            type="button"
            className="agent-modal-close"
            onClick={() => setDetailsOpen(false)}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <div className="agent-response-modal-body">
          {parsed ? (
            <>
              <PrettyValue value={parsed} depth={0} maxDepth={6} openAll={true} />
              {plain ? (
                <details className="agent-expand" open={false}>
                  <summary>Raw tool output</summary>
                  {codeLike ? (
                    <pre className="agent-param-pre"><code>{plain}</code></pre>
                  ) : (
                    <Markdown options={markdownOptions}>{plain}</Markdown>
                  )}
                </details>
              ) : null}
            </>
          ) : (
            codeLike ? (
              <pre className="agent-param-pre"><code>{plain}</code></pre>
            ) : (
              <Markdown options={markdownOptions}>{plain}</Markdown>
            )
          )}
        </div>
      </Modal>
    </div>
  );
}

function ActionThoughtCard({ parsed, markdownOptions }) {
  const params = parsed?.action?.parameters;
  const paramRows = useMemo(() => {
    if (params === undefined) return [];
    if (params === null || typeof params === 'string' || typeof params === 'number' || typeof params === 'boolean') {
      return [{ key: 'value', value: params }];
    }
    if (Array.isArray(params)) {
      return params.map((v, idx) => ({ key: String(idx), value: v }));
    }
    if (typeof params === 'object') {
      return Object.entries(params).map(([k, v]) => ({ key: k, value: v }));
    }
    return [{ key: 'value', value: String(params) }];
  }, [params]);

  if (!parsed) return null;

  const thoughtText = normalizeForMarkdown(parsed?.thought || '');
  const actionName = parsed?.action?.name || '';

  // For the Action parameters table: do not recursively expand. If a value is an object/array,
  // keep it as compact JSON so the table doesn't become deeply nested.
  const renderParamCell = (value) => {
    if (value === null) return <span className="agent-param-null">null</span>;
    if (value === undefined) return <span className="agent-param-null">—</span>;
    if (typeof value === 'string') {
      const s = value;
      const multiline = s.includes('\n') || s.length > 120;
      return multiline ? <pre className="agent-param-pre">{s}</pre> : <span className="agent-param-text">{s}</span>;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return <span className="agent-param-text">{String(value)}</span>;
    }
    // object/array: keep as JSON
    return <pre className="agent-param-pre">{formatParamScalar(value)}</pre>;
  };

  return (
    <div className="agent-thought-card">
      {thoughtText ? (
        <div className="agent-thought-block">
          <div className="agent-thought-label">Thought</div>
          <div className="agent-thought-text">
            <Markdown options={markdownOptions}>{thoughtText}</Markdown>
          </div>
        </div>
      ) : null}

      {(actionName || paramRows.length > 0) ? (
        <div className="agent-action-block">
          <div className="agent-action-header">
            <div className="agent-action-label">Action</div>
            {actionName ? <span className="agent-action-name">{actionName}</span> : <span className="agent-action-name muted">Unknown</span>}
          </div>
          {paramRows.length > 0 ? (
            <div className="agent-action-params">
              <div className="agent-params-label">Parameters</div>
              <table className="agent-params-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {paramRows.map((row) => (
                    <tr key={`param-${row.key}`}>
                      <td className="agent-param-key">{row.key}</td>
                      <td className="agent-param-val">{renderParamCell(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const buildActionSummaryMap = (transcript, keyFn) => {
  const summaryMap = new Map();
  if (!Array.isArray(transcript) || transcript.length === 0) {
    return summaryMap;
  }

  const getKey = typeof keyFn === 'function'
    ? keyFn
    : (msg, idx) => {
      const id = msg && typeof msg === 'object'
        ? (msg.id ?? msg.message_id ?? msg.uuid ?? msg._id ?? msg.created_at ?? msg.createdAt ?? msg.timestamp ?? msg.ts)
        : undefined;
      if (id !== undefined && id !== null && String(id).trim() !== '') return String(id);
      const role = msg?.role || '';
      const content = typeof msg?.content === 'string' ? msg.content : (() => {
        try { return JSON.stringify(msg?.content); } catch { return String(msg?.content || ''); }
      })();
      const meta = `${msg?._agentStep ?? ''}|${msg?.name ?? ''}`;
      return `idx-${idx}-h-${hashString(`${role}|${meta}|${content}`)}`;
    };

  let actionsSinceLastUser = [];

  for (let idx = 0; idx < transcript.length; idx += 1) {
    const msg = transcript[idx];
    if (!msg) continue;

    if (msg.role === 'user' || msg.role === 'system') {
      actionsSinceLastUser = [];
      continue;
    }

    if (msg.role === 'assistant') {
      const parsed = parseActionPayload(msg.content);
      if (parsed) {
        actionsSinceLastUser.push(parsed);
        continue;
      }

      const contentText = typeof msg.content === 'string' ? msg.content : '';
      const isActionResponse = /^\s*Action[_ ]Response\s+for/i.test(contentText);
      const isCompleteTaskResponse = /^\s*Action[_ ]Response\s+for\s+['"]?complete_task['"]?\s*:/i.test(contentText);
      // Normally tool responses are not where we want to show the collapsed action summary.
      // But `complete_task` is special: the final assistant answer can be embedded in the wrapper,
      // and we still want the small overview of actions shown for that last message.
      if (isActionResponse && !isCompleteTaskResponse) continue;

      if (actionsSinceLastUser.length > 0) {
        summaryMap.set(getKey(msg, idx), actionsSinceLastUser.slice());
      }
    }
  }

  return summaryMap;
};

function FanOutPayloadBlock({ jsonText }) {
  const formatted = useMemo(() => {
    if (typeof jsonText !== 'string') return '';
    const trimmed = jsonText.trim();
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (_err) {
      return trimmed;
    }
  }, [jsonText]);

  return (
    <details className="fanout-payload-details">
      <summary>Payload (fan_out_agents)</summary>
      <pre className="fanout-json-block">
        <code>{formatted}</code>
      </pre>
    </details>
  );
}

function FanOutResultView({ summaryText, payload, trailingText, markdownOptions, rawJson }) {
  const tasks = useMemo(() => (Array.isArray(payload?.tasks) ? payload.tasks : []), [payload]);
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const taskCount = tasks.length;

  useEffect(() => {
    setActiveTaskIndex((prev) => {
      if (taskCount === 0) return 0;
      return prev > taskCount - 1 ? 0 : prev;
    });
  }, [taskCount]);

  const activeTask = tasks[activeTaskIndex] || null;
  const cycleTask = (delta) => {
    if (!taskCount) return;
    setActiveTaskIndex((prev) => {
      const next = (prev + delta + taskCount) % taskCount;
      return next;
    });
  };

  const activeTaskActions = useMemo(() => (
    activeTask && Array.isArray(activeTask.actions) ? activeTask.actions : []
  ), [activeTask]);

  const fanInSummary = useMemo(() => {
    if (!payload?.fan_in) return '';
    const fanIn = payload.fan_in;
    if (typeof fanIn === 'string') return fanIn;
    if (fanIn && typeof fanIn === 'object') {
      const messages = Array.isArray(fanIn.messages) ? fanIn.messages : [];
      const assistantMsg = [...messages].reverse().find(
        (msg) => msg && typeof msg.content === 'string' && (msg.role || '').toLowerCase() === 'assistant'
      );
      if (assistantMsg) return assistantMsg.content;
      if (typeof fanIn.summary === 'string') return fanIn.summary;
      if (typeof fanIn.output === 'string') return fanIn.output;
    }
    return '';
  }, [payload]);

  const fanInError = payload?.fan_in && typeof payload.fan_in === 'object' ? payload.fan_in.error : undefined;
  const fanOutId = payload?.fan_out_id;
  const fanInActions = useMemo(() => {
    if (!payload?.fan_in || typeof payload.fan_in !== 'object') return [];
    const source = payload.fan_in;
    if (Array.isArray(source.actions)) return source.actions;
    return [];
  }, [payload]);

  const formatStatus = (value) => (value ? value.toString().toUpperCase() : 'UNKNOWN');
  const rawStatus = (value) => (value ? value.toString().toLowerCase() : 'unknown');

  const renderMetadata = (meta) => {
    if (!meta || typeof meta !== 'object') return null;
    try {
      return JSON.stringify(meta, null, 2);
    } catch (err) {
      return String(meta);
    }
  };

  return (
    <div className="fanout-result">
      <div className="fanout-submessage fanout-submessage--summary">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Fan-out Invocation</span>
          {fanOutId && <span className="fanout-id">ID: {fanOutId}</span>}
        </div>
        {summaryText ? (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(summaryText)}</Markdown>
        ) : (
          <p className="fanout-empty-text">No invocation details provided.</p>
        )}
      </div>

      <div className="fanout-submessage fanout-submessage--tasks">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Parallel Agents</span>
          <span className="fanout-section-meta">{tasks.length || 0} running</span>
        </div>
        {tasks.length === 0 ? (
          <div className="fanout-empty-text">No agent runs were reported.</div>
        ) : (
          <>
            {taskCount > 1 && (
              <div className="fanout-task-controls">
                <button
                  type="button"
                  className="fanout-cycle-btn"
                  onClick={() => cycleTask(-1)}
                  aria-label="Show previous agent"
                >
                  ‹
                </button>
                <span className="fanout-task-position">{activeTaskIndex + 1} / {taskCount}</span>
                <button
                  type="button"
                  className="fanout-cycle-btn"
                  onClick={() => cycleTask(1)}
                  aria-label="Show next agent"
                >
                  ›
                </button>
              </div>
            )}
            <div className="fanout-task-tabs">
              {tasks.map((task, idx) => (
                <button
                  key={`${task?.name || 'task'}-${idx}`}
                  type="button"
                  className={`fanout-task-tab${idx === activeTaskIndex ? ' active' : ''}`}
                  onClick={() => setActiveTaskIndex(idx)}
                >
                  <span className="fanout-task-tab-name">{task?.name || `Agent ${idx + 1}`}</span>
                  <span className={`fanout-task-tab-status status-${rawStatus(task?.status)}`}>
                    {formatStatus(task?.status)}
                  </span>
                </button>
              ))}
            </div>
            {activeTask && (
              <div className="fanout-task-body">
                <div className="fanout-task-meta">
                  <div className="fanout-task-meta-name">
                    <strong>{activeTask.name || `Agent ${activeTaskIndex + 1}`}</strong>
                  </div>
                  <div className="fanout-task-meta-times">
                    {activeTask.started_at && (
                      <span className="fanout-task-timestamp">Start: {activeTask.started_at}</span>
                    )}
                    {activeTask.completed_at && (
                      <span className="fanout-task-timestamp">Finish: {activeTask.completed_at}</span>
                    )}
                  </div>
                </div>
                {activeTask.metadata && (
                  <pre className="fanout-task-metadata">{renderMetadata(activeTask.metadata)}</pre>
                )}
                {activeTask.error && (
                  <div className="fanout-task-error">{activeTask.error}</div>
                )}
                {activeTask.output ? (
                  <Markdown options={markdownOptions}>{normalizeForMarkdown(activeTask.output)}</Markdown>
                ) : (
                  <p className="fanout-task-empty">No assistant response captured.</p>
                )}
                {activeTaskActions.length > 0 && (
                  <details className="fanout-task-actions">
                    <summary>Thoughts &amp; actions ({activeTaskActions.length})</summary>
                    <ol className="fanout-task-actions-list">
                      {activeTaskActions.map((step, stepIdx) => {
                        const thoughtText = normalizeForMarkdown(step?.thought || '');
                        const actionName = step?.action?.name;
                        const actionParams = step?.action?.parameters;
                        let paramsDisplay = '';
                        if (actionParams !== undefined) {
                          if (typeof actionParams === 'string') {
                            paramsDisplay = actionParams;
                          } else {
                            try {
                              paramsDisplay = JSON.stringify(actionParams, null, 2);
                            } catch (_err) {
                              paramsDisplay = String(actionParams);
                            }
                          }
                        }

                        return (
                          <li key={`task-action-${stepIdx}`} className="fanout-task-action-item">
                            {thoughtText && (
                              <div className="fanout-task-thought">
                                <strong>Thought:</strong>
                                <Markdown options={markdownOptions}>{thoughtText}</Markdown>
                              </div>
                            )}
                            {(actionName || paramsDisplay) && (
                              <div className="fanout-task-action">
                                <strong>Action:</strong>
                                <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                                {paramsDisplay && (
                                  <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </details>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="fanout-submessage fanout-submessage--aggregation">
        <div className="fanout-subheader">
          <span className="fanout-section-title">Aggregation</span>
          {payload?.fan_in?.status && (
            <span className={`fanout-section-meta status-${rawStatus(payload.fan_in.status)}`}>
              {formatStatus(payload.fan_in.status)}
            </span>
          )}
        </div>
        {fanInSummary ? (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(fanInSummary)}</Markdown>
        ) : (
          <p className="fanout-empty-text">No aggregation output was returned.</p>
        )}
        {fanInError && <div className="fanout-task-error">{fanInError}</div>}
        {fanInActions.length > 0 && (
          <details className="fanout-task-actions">
            <summary>Aggregation thoughts &amp; actions ({fanInActions.length})</summary>
            <ol className="fanout-task-actions-list">
              {fanInActions.map((step, stepIdx) => {
                const thoughtText = normalizeForMarkdown(step?.thought || '');
                const actionName = step?.action?.name;
                const actionParams = step?.action?.parameters;
                let paramsDisplay = '';
                if (actionParams !== undefined) {
                  if (typeof actionParams === 'string') {
                    paramsDisplay = actionParams;
                  } else {
                    try {
                      paramsDisplay = JSON.stringify(actionParams, null, 2);
                    } catch (_err) {
                      paramsDisplay = String(actionParams);
                    }
                  }
                }

                return (
                  <li key={`fanin-action-${stepIdx}`} className="fanout-task-action-item">
                    {thoughtText && (
                      <div className="fanout-task-thought">
                        <strong>Thought:</strong>
                        <Markdown options={markdownOptions}>{thoughtText}</Markdown>
                      </div>
                    )}
                    {(actionName || paramsDisplay) && (
                      <div className="fanout-task-action">
                        <strong>Action:</strong>
                        <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                        {paramsDisplay && (
                          <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </details>
        )}
        {trailingText && (
          <Markdown options={markdownOptions}>{normalizeForMarkdown(trailingText)}</Markdown>
        )}
      </div>

      {/* Fallback if nothing rendered */}
      {!summaryText && tasks.length === 0 && !fanInSummary && (
        <FanOutPayloadBlock jsonText={rawJson} />
      )}
    </div>
  );
}

/**
 * Generic message list component.
 */
export function ChatMessagesList({
  messages = [],
  loading = false,
  error = '',
  hasMore = false,
  onLoadMore,
  markdownOptions,
  onLoadAgentStep,
  showAgentStepButtons = false,
  chatId,
  containerRef,
  scrollContainerRef, // optional outer scrollable container
  displayedCount = 0,
  allCount = 0,
  alignUserRight = true,
  onUUIDClick,
  seeThoughtsAndActions = false,
  fullTranscript = [],
}) {
  const getStableMessageKey = (msg, fallbackIndex) => {
    const prefix = chatId || 'new';
    if (!msg || typeof msg !== 'object') return `${prefix}-idx-${fallbackIndex}`;

    const id = msg.id ?? msg.message_id ?? msg.uuid ?? msg._id ?? msg.created_at ?? msg.createdAt ?? msg.timestamp ?? msg.ts;
    if (id !== undefined && id !== null && String(id).trim() !== '') {
      return `${prefix}-id-${String(id)}`;
    }

    // Fall back to hashing role+content+some metadata so the key stays stable across re-slices.
    const role = msg.role || '';
    const content = typeof msg.content === 'string' ? msg.content : (() => {
      try { return JSON.stringify(msg.content); } catch { return String(msg.content || ''); }
    })();
    const meta = `${msg._agentStep ?? ''}|${msg.name ?? ''}`;
    return `${prefix}-h-${hashString(`${role}|${meta}|${content}`)}`;
  };

  // Helper: extract unique UUID tokens from a text block
  const extractUUIDs = (text) => {
    if (!text || typeof text !== 'string') return [];
    const re = /@@([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})@@/g;
    const out = new Set();
    let m;
    while ((m = re.exec(text)) !== null) out.add(m[1]);
    return Array.from(out);
  };
  // --- Auto-scroll management ---
  const prevLenRef = useRef(messages.length);
  const prevLastMsgRef = useRef(null);
  const prevLastMsgContentLenRef = useRef(0);
  // Sticky-to-bottom: only auto-scroll when user is already at (or near) the bottom.
  // Track this via scroll events so we know the state *before* new content arrives.
  const wasAtBottomRef = useRef(true);
  const [copiedBlockId, setCopiedBlockId] = useState(null);

  // Track manual scroll position (only auto-scroll when user is near the bottom)
  useEffect(() => {
    const el = (scrollContainerRef?.current) || containerRef?.current;
    if (!el) return;
    const handler = () => {
      const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
      // Consider the user "at bottom" when they're close enough.
      // Keep this threshold modest so scrolling up even a bit disables auto-scroll.
      wasAtBottomRef.current = distance < 120;
    };
    el.addEventListener('scroll', handler, { passive: true });
    // initialize once
    handler();
    return () => el.removeEventListener('scroll', handler);
  }, [scrollContainerRef, containerRef]);

  useEffect(() => {
    const el = (scrollContainerRef?.current) || containerRef?.current;
    if (!el) return;
    const prevLen = prevLenRef.current;
    const newLen = messages.length;
    if (newLen === 0) { prevLenRef.current = newLen; prevLastMsgRef.current = null; prevLastMsgContentLenRef.current = 0; return; }
    const lastMsg = messages[newLen - 1];

    // Was the user anchored at/near bottom *before* this update?
    const shouldStickToBottom = wasAtBottomRef.current === true;
    const appended = newLen > prevLen; // a brand new message object appended

    // Detect streaming growth of last assistant message (same object reference, longer content)
    const prevLastRef = prevLastMsgRef.current;
    const prevContentLen = prevLastMsgContentLenRef.current;
    const currentContentLen = (lastMsg?.content && typeof lastMsg.content === 'string') ? lastMsg.content.length : 0;
    const sameLastObject = prevLastRef === lastMsg; // reference equality
    const grew = sameLastObject && currentContentLen > prevContentLen;

    const role = lastMsg?.role;
    const shouldScroll = (
      // Always scroll for new user message
      (appended && role === 'user') ||
      // For assistant: scroll only if user was already at/near bottom
      (appended && role === 'assistant' && shouldStickToBottom) ||
      // Streaming growth of assistant message while user anchored at bottom
      (!appended && role === 'assistant' && grew && shouldStickToBottom)
    );

    if (shouldScroll) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }

    // Update refs for next tick
    prevLenRef.current = newLen;
    prevLastMsgRef.current = lastMsg;
    prevLastMsgContentLenRef.current = currentContentLen;
  }, [messages, containerRef, scrollContainerRef]);

  // --- Code block override with copy button & lazy highlight.js ---
  const hljsRef = useRef(null);
  const highlightQueueRef = useRef([]);

  useEffect(() => {
    if (highlightQueueRef.current.length === 0) return;
    let cancelled = false;
    const run = async () => {
      if (!hljsRef.current) {
        try {
          const mod = await import('highlight.js');
          hljsRef.current = mod?.default || mod;
        } catch (e) {
          // highlight.js not available; skip silently
          highlightQueueRef.current = [];
          return;
        }
      }
      if (cancelled) return;
      highlightQueueRef.current.forEach(codeEl => {
        try { hljsRef.current?.highlightElement(codeEl); } catch (_) { /* ignore */ }
      });
      highlightQueueRef.current = [];
    };
    run();
    return () => { cancelled = true; };
  }, [messages]);

  const handleCopy = (text, id) => {
    try { navigator.clipboard.writeText(text); } catch (_) {
      // fallback selection
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedBlockId(id);
    setTimeout(() => setCopiedBlockId(prev => prev === id ? null : prev), 1400);
  };

  const CodePre = ({ className = '', children, ...rest }) => {
    // children might be <code> element produced by markdown-to-jsx
    let codeText = '';
    let language = '';
    const child = Array.isArray(children) ? children[0] : children;
    const codeElement = (child && child.props && child.props.children) ? child : null;

    // markdown-to-jsx (and our node processors) can sometimes provide code children as arrays of
    // React nodes. Joining those directly produces "[object Object]". Flatten to plain text.
    const extractPlainText = (node) => {
      if (node === null || node === undefined) return '';
      if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') return String(node);
      if (Array.isArray(node)) return node.map(extractPlainText).join('');
      if (React.isValidElement(node)) return extractPlainText(node.props?.children);
      return '';
    };

    if (codeElement && codeElement.props) {
      codeText = extractPlainText(codeElement.props.children);
      const cls = codeElement.props.className || '';
      const match = cls.match(/language-([a-z0-9#+-_]+)/i);
      if (match) language = match[1];
    }
    const blockId = useRef(`cb_${Math.random().toString(36).slice(2, 10)}`);
    const codeRef = useRef(null);
    useEffect(() => {
      if (codeRef.current) highlightQueueRef.current.push(codeRef.current);
    }, [codeText]);

    return (
      <div className={`code-block`} data-lang={language}>
        <button
          type="button"
          className={`copy-code-btn${copiedBlockId === blockId.current ? ' copied' : ''}`}
          onClick={() => handleCopy(codeText, blockId.current)}
          aria-label="Copy code"
        >
          {copiedBlockId === blockId.current ? 'Copied' : 'Copy'}
        </button>
        <pre className={className || ''} {...rest}>
          {codeElement ? (
            <code ref={codeRef} className={codeElement.props.className}>
              {codeText}
            </code>
          ) : children}
        </pre>
      </div>
    );
  };

  const mergedMarkdownOptions = useMemo(() => {
    const base = markdownOptions || {};
    return {
      ...base,
      overrides: {
        ...(base.overrides || {}),
        pre: { component: CodePre },
      }
    };
  }, [markdownOptions, copiedBlockId, CodePre]);

  const actionSummaryMap = useMemo(() => {
    if (seeThoughtsAndActions) {
      return new Map();
    }
    return buildActionSummaryMap(fullTranscript, (msg, idx) => getStableMessageKey(msg, idx));
  }, [fullTranscript, seeThoughtsAndActions, getStableMessageKey]);

  return (
    <div className="messages-list" ref={containerRef}>
      {error && <div className="chat-error-message">{error}</div>}
      {hasMore && (
        <div className="load-more-wrapper" style={{ textAlign: 'center', margin: '10px 0' }}>
          <button className="load-more-button" onClick={onLoadMore} disabled={loading}>
            Load Older Messages ({Math.max(0, allCount.length - displayedCount.length)} more)
          </button>
        </div>
      )}
      {messages.map((m, i) => {
        const isUser = m.role === 'user';
        const isAssistant = m.role === 'assistant';
        const contentStr = coerceMessageContentToText(m.content);
        let uuidSearchBase = contentStr;
        // keep modern `.message` classes but also include legacy `.message-bubble`
        const cls = `message message-bubble ${m.role}${isUser && alignUserRight ? ' right' : ''}`;

        // Special-case: complete_task should always render in full as plain markdown.
        // Do not parse/split/transform it, otherwise we risk hiding or omitting content.
        const isCompleteTaskActionResponse = isAssistant
          && /^\s*Action[_ ]Response\s+for\s+['"]?complete_task['"]?\s*:/i.test(contentStr);

        // If this assistant message is a thought/action payload, render it in a human-friendly layout.
        // This primarily matters when thoughts/actions are fully visible.
        const parsedAction = (!isCompleteTaskActionResponse && isAssistant && (seeThoughtsAndActions || contentStr.trim().startsWith('{') || contentStr.trim().startsWith('```')))
          ? parseActionPayload(contentStr)
          : null;

        // If this assistant message is an Action_Response from a tool call, render it in a structured way.
        // (Except for complete_task, which is handled above and should render as normal assistant text.)
        const parsedActionResponse = (!isCompleteTaskActionResponse && !parsedAction && isAssistant && /^\s*Action[_ ]Response\s+for/i.test(contentStr) && !contentStr.includes('fan_out_agents'))
          ? parseActionResponseMessage(contentStr)
          : null;

        const payloadExtraction = (parsedAction || parsedActionResponse) ? null : extractFanOutPayload(contentStr);
        let contentSegments = [];
        let parsedFanOut = null;
        if (isCompleteTaskActionResponse) {
          const cleaned = stripLeadingCompleteTaskHeaderLine(contentStr);
          const splitRefs = splitTopLevelReferencesSection(cleaned);
          if (splitRefs) {
            if (splitRefs.main) contentSegments.push({ type: 'markdown', text: splitRefs.main });
            contentSegments.push({ type: 'references', text: splitRefs.references });
          } else {
            contentSegments = [{ type: 'markdown', text: cleaned }];
          }
          uuidSearchBase = cleaned;
        } else if (parsedAction) {
          contentSegments = [{ type: 'actionThought', data: parsedAction }];
          // Include a readable summary base for UUID extraction if any
          uuidSearchBase = `${parsedAction?.thought || ''}\n${parsedAction?.action?.name || ''}\n${formatParamScalar(parsedAction?.action?.parameters)}`;
        } else if (parsedActionResponse) {
          contentSegments = [{ type: 'actionResponse', data: parsedActionResponse }];
          uuidSearchBase = `${parsedActionResponse?.toolName || ''}\n${parsedActionResponse?.payloadText || ''}`;
        } else if (payloadExtraction) {
          const { summary, jsonText, trailing } = payloadExtraction;
          let payloadObject = null;
          try {
            payloadObject = JSON.parse(jsonText);
          } catch (_err) {
            payloadObject = null;
          }

          if (payloadObject) {
            parsedFanOut = {
              summary,
              trailing,
              payload: payloadObject,
              raw: jsonText,
            };

            const uuidPieces = [];
            if (summary) uuidPieces.push(summary);
            if (trailing) uuidPieces.push(trailing);
            const tasks = Array.isArray(payloadObject.tasks) ? payloadObject.tasks : [];
            tasks.forEach((task) => {
              if (task && typeof task.output === 'string') uuidPieces.push(task.output);
              if (task && typeof task.error === 'string') uuidPieces.push(task.error);
              if (task && Array.isArray(task.actions)) {
                task.actions.forEach((step) => {
                  if (step && typeof step.thought === 'string') uuidPieces.push(step.thought);
                  const parameters = step?.action?.parameters;
                  if (typeof parameters === 'string') {
                    uuidPieces.push(parameters);
                  }
                });
              }
            });
            const fanIn = payloadObject.fan_in;
            if (fanIn) {
              if (typeof fanIn === 'string') {
                uuidPieces.push(fanIn);
              } else if (typeof fanIn === 'object') {
                if (Array.isArray(fanIn.messages)) {
                  fanIn.messages.forEach((msg) => {
                    if (msg && typeof msg.content === 'string') uuidPieces.push(msg.content);
                  });
                }
                if (typeof fanIn.summary === 'string') uuidPieces.push(fanIn.summary);
                if (typeof fanIn.output === 'string') uuidPieces.push(fanIn.output);
                if (typeof fanIn.error === 'string') uuidPieces.push(fanIn.error);
                if (Array.isArray(fanIn.actions)) {
                  fanIn.actions.forEach((step) => {
                    if (step && typeof step.thought === 'string') uuidPieces.push(step.thought);
                    const parameters = step?.action?.parameters;
                    if (typeof parameters === 'string') {
                      uuidPieces.push(parameters);
                    }
                  });
                }
              }
            }
            uuidSearchBase = uuidPieces.join('\n');

            contentSegments = [{ type: 'fanoutRich', data: parsedFanOut }];
          } else {
            uuidSearchBase = `${summary}\n${jsonText}\n${trailing}`;
            if (summary) {
              contentSegments.push({ type: 'markdown', text: summary });
            }
            contentSegments.push({ type: 'fanoutRaw', text: jsonText });
            if (trailing) {
              contentSegments.push({ type: 'markdown', text: trailing });
            }
          }
        } else {
          contentSegments = [{ type: 'markdown', text: contentStr }];
        }

        const msgUUIDs = extractUUIDs(uuidSearchBase);
        const summaryActions = actionSummaryMap.get(getStableMessageKey(m, i)) || [];
        const hasFanOutView = contentSegments.some((segment) => segment.type === 'fanoutRich');
        const showCollapsedSummary = !seeThoughtsAndActions && isAssistant && summaryActions.length > 0 && !hasFanOutView;

        return (
          <div key={getStableMessageKey(m, i)} className={cls}>
            <div className="message-content">
              {/* Header row: role label on the left, optional step icon on the right */}
              <div className="message-header">
                <div className="message-role"><strong>{isUser ? 'You' : isAssistant ? 'Assistant' : (m.role || '')}</strong></div>
                {showAgentStepButtons && isAssistant && m._agentStep && (
                  <button
                    className="resume-step-icon-btn"
                    onClick={() => onLoadAgentStep?.(m._agentStep)}
                    title={`Reload chat at step ${m._agentStep}`}
                    aria-label={`Reload chat at step ${m._agentStep}`}
                  >
                    {/* Inline SVG icon (history/restore) */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M12 5a7 7 0 1 1-6.364 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 8v4l3 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 5v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 5H3l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                )}
              </div>
              {showCollapsedSummary && (
                <>
                <details className="fanout-task-actions agent-thoughts-summary">
                  <summary>Thoughts &amp; actions ({summaryActions.length})</summary>
                  <ol className="fanout-task-actions-list">
                    {summaryActions.map((step, stepIdx) => {
                      const thoughtText = normalizeForMarkdown(step?.thought || '');
                      const actionName = step?.action?.name;
                      const parametersValue = step?.action?.parameters;
                      let paramsDisplay = '';
                      if (parametersValue !== undefined) {
                        if (typeof parametersValue === 'string') {
                          paramsDisplay = parametersValue;
                        } else if (
                          typeof parametersValue === 'number' ||
                          typeof parametersValue === 'boolean' ||
                          parametersValue === null
                        ) {
                          paramsDisplay = String(parametersValue);
                        } else {
                          try {
                            paramsDisplay = JSON.stringify(parametersValue, null, 2);
                          } catch (_err) {
                            paramsDisplay = String(parametersValue);
                          }
                        }
                      }

                      return (
                        <li key={`agent-summary-${i}-${stepIdx}`} className="fanout-task-action-item">
                          {thoughtText && (
                            <div className="fanout-task-thought">
                              <strong>Thought:</strong>
                              <Markdown options={mergedMarkdownOptions}>{thoughtText}</Markdown>
                            </div>
                          )}
                          {(actionName || paramsDisplay) && (
                            <div className="fanout-task-action">
                              <strong>Action:</strong>
                              <span className="fanout-task-action-name">{actionName || 'Unknown action'}</span>
                              {paramsDisplay && (
                                <pre className="fanout-task-action-params">{paramsDisplay}</pre>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </details>
                {/* add an empty line */}
                <div style={{ height: '1em' }} />
                </>
              )}
              {contentSegments.map((segment, segmentIdx) => {
                if (segment.type === 'actionThought') {
                  return (
                    <ActionThoughtCard
                      key={`action-thought-${i}-${segmentIdx}`}
                      parsed={segment.data}
                      markdownOptions={mergedMarkdownOptions}
                    />
                  );
                }
                if (segment.type === 'actionResponse') {
                  return (
                    <ActionResponseCard
                      key={`action-response-${i}-${segmentIdx}`}
                      toolName={segment.data.toolName}
                      parsed={segment.data.parsed}
                      payloadText={segment.data.payloadText}
                      markdownOptions={mergedMarkdownOptions}
                    />
                  );
                }
                if (segment.type === 'fanoutRaw') {
                  return (
                    <FanOutPayloadBlock
                      key={`fanout-${i}-${segmentIdx}`}
                      jsonText={segment.text}
                    />
                  );
                }
                if (segment.type === 'fanoutRich') {
                  return (
                    <FanOutResultView
                      key={`fanout-rich-${i}-${segmentIdx}`}
                      summaryText={segment.data.summary}
                      payload={segment.data.payload}
                      trailingText={segment.data.trailing}
                      markdownOptions={mergedMarkdownOptions}
                      rawJson={segment.data.raw}
                    />
                  );
                }
                if (segment.type === 'references') {
                  return (
                    <details
                      key={`references-${i}-${segmentIdx}`}
                      className="final-references"
                      open
                    >
                      <summary>References</summary>
                      <Markdown options={mergedMarkdownOptions}>
                        {normalizeForMarkdown(segment.text)}
                      </Markdown>
                    </details>
                  );
                }
                return (
                  <Markdown
                    key={`markdown-${i}-${segmentIdx}`}
                    options={mergedMarkdownOptions}
                  >
                    {normalizeForMarkdown(segment.text)}
                  </Markdown>
                );
              })}
              
              {/* Bottom chip bar mirrors the top so users have easy access after reading */}
              {msgUUIDs.length > 0 && (
                <div className="uuid-chipbar bottom">
                  {msgUUIDs.map(u => (
                    <button
                      type="button"
                      key={`bottom-${i}-${u}`}
                      className="uuid-chip"
                      title={`Open Exploration Log (${u})`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUUIDClick && onUUIDClick(u); }}
                    >
                      {`Explore: ${u.substring(0, 8)}…`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {loading && (
        <div className="loading">
          <span className="loading-spinner" aria-hidden="true" />
          <span>Assistant is thinking...</span>
        </div>
      )}
    </div>
  );
}

export default ChatMessagesList;
